import { format } from 'date-fns'
import { OnlyViewHoc } from 'hocs'
import debounce from 'lodash.debounce'
import * as React from 'react'

import { getFio } from 'helpers/formatText'
import { useMutation } from 'hooks/useMutation'
import { usePagination } from 'hooks/usePagination'
import { useStores } from 'hooks/useStores'
import {
  useGetMainBranchesQuery,
  useGetRegionsQuery,
  useGetRolesQuery,
  useGetSubvisionsQuery,
  useGetUnitBranchesQuery,
} from 'redux/services/common'
import { UsersApi, UsersResetPasswordData } from 'services/api/UsersApi'

import { AppButton } from 'components/AppButton'
import { AppLayout } from 'components/AppLayout'
import { AppLayoutTable } from 'components/AppLayoutTable'
import { AppPagination } from 'components/AppPagination'
import { AppTable } from 'components/AppTable'

import { DivisionType } from 'models/common'
import { User, UserDTO } from 'models/user'

import { ReactComponent as UserCircleAddIcon } from 'assets/images/svg/user_circle_add.svg'

import styles from './UsersPage.module.scss'
import UsersDeleteModal from './components/UsersDeleteModal'
import { UsersFormModal } from './components/UsersFormModal'
import { UsersResetPassword } from './components/UsersResetPassword'

enum ModalState {
  None,
  Edit,
  ResetPassword,
}
export const UsersPage: React.FC = () => {
  const { data: roles = [] } = useGetRolesQuery()
  const { data: regions = [] } = useGetRegionsQuery()
  const { data: subvisions = [] } = useGetSubvisionsQuery()
  const { data: mainBranches = [] } = useGetMainBranchesQuery()
  const { data: unitBranches = [] } = useGetUnitBranchesQuery()

  const { dialogStore } = useStores()
  const [disabled, setDisabled] = React.useState(false)
  const [selectedUser, setSelectedUser] = React.useState<User | null>(null)
  const [modal, setModal] = React.useState<ModalState>(ModalState.None)
  const [deleteModalUser, setDeleteModalUser] = React.useState<User | null>(
    null,
  )
  const { page, list, count, setPage, getList, setList, setParams } =
    usePagination(UsersApi.getUsers)
  const deleteUserMutatuion = useMutation(
    ({ phone, reason }: { phone: string; reason: string }) =>
      UsersApi.deleteUser(phone, reason),
  )

  const debouncedGetList = React.useRef(
    debounce((search: string) => {
      getList(0, {
        search,
      })
    }, 300),
  )

  const openModal = () => {
    setModal(ModalState.Edit)
    document.body.style.overflow = 'hidden'
  }

  const closeModal = () => {
    setModal(ModalState.None)
    document.body.style.overflow = 'auto'

    setSelectedUser(null)
  }

  const openEditModal = async (user: User) => {
    try {
      setSelectedUser(user)
      setModal(ModalState.Edit)
    } catch (error) {}
  }

  const openResetPasswordModal = (user: User) => {
    setSelectedUser(user)
    setModal(ModalState.ResetPassword)
  }

  const uploadUserAvatar = async (phone: string, file: File) => {
    try {
      const fd = new FormData()
      fd.append('avatar', file)
      await UsersApi.patchUser(phone, fd)
    } catch (error) {}
  }

  const approveUser = async (phone: string) => {
    try {
      setDisabled(true)

      await UsersApi.approveRegistration(phone)

      setList(prev => {
        const users = prev.slice()
        const foundedUser = users.find(item => item.phone === phone)
        if (foundedUser) {
          foundedUser.is_active = true
        }
        return users
      })
    } catch (error) {
    } finally {
      setDisabled(false)
    }
  }

  const onSaveUser = async (user: UserDTO, file: File | null) => {
    if (user.type_division === DivisionType.M) {
      delete user.branches
    } else {
      delete user.main_branch
    }

    try {
      setDisabled(true)
      if (selectedUser) {
        await UsersApi.updateUser(user)
      } else {
        await UsersApi.signUp(user)
      }
      if (file) {
        await uploadUserAvatar(user.phone, file)
      }
      dialogStore.openModal({
        title: 'Успех',
        description: selectedUser
          ? 'Пользователь отредактирован'
          : 'Пользователь добавлен',
      })
      closeModal()

      getList(1)
      setPage(1)
    } catch (error) {
      setDisabled(false)
      return error
    } finally {
      setDisabled(false)
    }
  }


  const resetPassword = async (values: UsersResetPasswordData) => {
    try {
      setDisabled(true)
      await UsersApi.resetPassword(selectedUser?.phone as string, values)
      dialogStore.openModal({
        title: 'Успех',
        description: `Пароль успешно сменен для ${selectedUser?.phone}`,
      })
      getList(1)
      setPage(1)
      closeModal()
    } catch (error) {
    } finally {
      setDisabled(false)
    }
  }

  const deleteUser = (user: User, reason: string) => {
    deleteUserMutatuion
      .mutate({
        phone: user.phone,
        reason,
      })
      .then(() => {
        getList()
        setDeleteModalUser(null)
      })
  }

  const onChangeQuery = (search: string) => {
    setParams({
      search,
    })
    debouncedGetList?.current?.(search)
  }

  return (
    <>
      <AppLayout
        linksRight={[
          {
            title: 'Добавить пользователя',
            icon: <UserCircleAddIcon />,
            onClick: openModal,
          },
        ]}
      />
      <AppLayoutTable onChangeQuery={onChangeQuery} title="Пользователи">
        <AppTable
          headers={[
            {
              data: 'Ф.И.О',
              key: 'first_name',
              render: (_, user) => {
                return (
                  <div>
                    {getFio(user)}

                    {user.last_login && (
                      <>
                        <br />
                        <sup>
                          {format(
                            new Date(user.last_login),
                            'yyyy-MM-dd HH:mm',
                          )}
                        </sup>
                      </>
                    )}
                  </div>
                )
              },
            },
            {
              data: 'Номер телефона',
              key: 'phone',
              render: (_, user) => {
                return (
                  <a href={`tel:${user.phone}`} style={{ color: '#427DBA' }}>
                    {user.phone}
                  </a>
                )
              },
            },
            {
              data: 'Телеграм',
              key: 'tgm_username',
              render: (_, user) => {
                return (
                  <a
                    href={`https://t.me/${user.tgm_username}`}
                    style={{ color: '#427DBA' }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {user.tgm_username}
                  </a>
                )
              },
            },
            {
              data: 'Статус',
              key: 'inn',
              width: 80,
              render: (_, data) => {
                const color = data.is_active ? '#03d01f' : '#F82B2B'
                return (
                  <span style={{ color }}>
                    {data.is_active ? 'Активен' : 'Не активен'}
                  </span>
                )
              },
            },
            {
              data: 'Регион',
              key: 'region',
              width: 80,
              render: (_, data) => {
                return data?.region?.name || 'Не выбрано'
              },
            },
            {
              data: 'Роль',
              key: 'role',
              width: 100,
              render: (_, data) => {
                return roles.find(role => role.code === data?.role)?.name || ''
              },
            },
            {
              data: 'Действия',
              key: 'id',
              render: (_, user: User) => {
                return (
                  <div className={styles.buttons}>
                    <AppButton
                      onClick={() => openEditModal(user)}
                      variant="siren_dashed_border"
                      size="small"
                    >
                      Редактировать
                    </AppButton>
                    <OnlyViewHoc>
                      <AppButton
                        onClick={() => openResetPasswordModal(user)}
                        variant="siren"
                        size="small"
                      >
                        Сбросить пароль
                      </AppButton>
                      {user?.is_active ? (
                        <AppButton
                          onClick={() => setDeleteModalUser(user)}
                          variant="red"
                          size="small"
                        >
                          Заблокировать
                        </AppButton>
                      ) : (
                        <AppButton
                          disabled={disabled}
                          onClick={() => approveUser(user.phone)}
                          variant="yellow"
                          size="small"
                        >
                          Разблокировать
                        </AppButton>
                      )}
                    </OnlyViewHoc>
                  </div>
                )
              },
            },
          ]}
          dataSource={list}
        />
        <AppPagination
          forcePage={page - 1}
          onPageChange={getList}
          count={count}
        />
      </AppLayoutTable>

      {modal === ModalState.Edit ? (
        <UsersFormModal
          title={
            selectedUser
              ? 'Редактировать пользователя'
              : 'Добавить пользователь'
          }
          button={selectedUser ? 'Редактировать' : 'Добавить'}
          user={selectedUser}
          roles={roles}
          regions={regions}
          subvisions={subvisions}
          mainBranchs={mainBranches}
          unitsBranchs={unitBranches}
          disabled={disabled}
          onSave={onSaveUser}
          onClose={closeModal}
        />
      ) : null}
      {modal === ModalState.ResetPassword ? (
        <UsersResetPassword
          onSubmit={resetPassword}
          onClose={closeModal}
          disabled={disabled}
          user={selectedUser as User}
        />
      ) : null}
      {deleteModalUser && (
        <UsersDeleteModal
          onClose={() => setDeleteModalUser(null)}
          onDelete={deleteUser}
          user={deleteModalUser}
          disabled={deleteUserMutatuion.mutating}
        />
      )}
    </>
  )
}
